import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Grid3x3, Info } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import TenantSelect from '../components/TenantSelect';

// Heatmap di copertura MITRE ATT&CK (2026-09-24, proposta 5 del framework di detection):
// 14 colonne = tattiche in ordine di kill chain; ogni tessera = una tecnica del catalogo (o
// referenziata da regole/case). Quattro stati, con un solo colore "caldo" (ambra, scala per
// numero di alert) riservato all'attività coperta; il resto in toni freddi/neutri:
//   active    regola + attività nel periodo — ambra, più scuro = più alert
//   observed  attività senza regola nostra (EDR/Athena/Entra portano la tecnica) — bordo viola
//   covered   regola presente, nessuna attività — azzurro chiaro
//   uncovered nessuna regola, nessuna attività — grigio: il buco
type State = 'active' | 'covered' | 'observed' | 'uncovered';
interface Technique { id: string; name: string; rules: string[]; alerts: number; cases: number; state: State }
interface Tactic { name: string; techniques: Technique[]; covered: number; active: number; total: number }
interface Coverage {
  days: number;
  tactics: Tactic[];
  summary: { tacticsCovered: number; tacticsActive: number; techniquesCovered: number; techniquesActive: number; techniquesObserved: number; techniquesTotal: number };
}

const STATE_LABEL: Record<State, string> = { active: 'Covered, with activity', observed: 'Activity without our rule', covered: 'Covered, no activity', uncovered: 'Not covered' };

function tileClass(t: Technique, maxAlerts: number): string {
  switch (t.state) {
    case 'active': {
      const level = maxAlerts <= 1 ? 3 : Math.min(4, 1 + Math.floor((3 * Math.log1p(t.alerts)) / Math.log1p(maxAlerts)));
      return ['bg-amber-100 text-amber-900', 'bg-amber-200 text-amber-900', 'bg-amber-300 text-amber-950', 'bg-amber-500 text-white', 'bg-amber-700 text-white'][level];
    }
    case 'observed': return 'bg-white text-violet-800 border-2 border-violet-400';
    case 'covered': return 'bg-sky-100 text-sky-900';
    default: return 'bg-gray-100 text-gray-400';
  }
}

export default function MitreCoveragePage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{ tactic: string; technique: Technique } | null>(null);
  const [hideUncovered, setHideUncovered] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/mitre/coverage', { params: { tenantId: selectedTenantId ?? undefined, days } })
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, [selectedTenantId, days]);

  const maxAlerts = useMemo(() => Math.max(1, ...(data?.tactics.flatMap(t => t.techniques.map(x => x.alerts)) || [1])), [data]);
  const gaps = useMemo(() => {
    if (!data) return [];
    // Buchi che pesano di più: tattiche con nessuna regola, poi tecniche "observed" (c'è il dato, manca la regola).
    return data.tactics.filter(t => t.covered === 0).map(t => t.name);
  }, [data]);
  const observed = useMemo(() => data?.tactics.flatMap(t => t.techniques.filter(x => x.state === 'observed').map(x => ({ tactic: t.name, ...x }))) || [], [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2"><Grid3x3 size={20} /> MITRE ATT&CK Coverage</h1>
            <TenantSelect />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Which techniques our enabled rules cover, and which ones actually fired in the period. Grey tiles are the gaps; violet ones
            are techniques seen through a vendor (EDR, Athena, Microsoft 365) without a rule of our own.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={hideUncovered} onChange={e => setHideUncovered(e.target.checked)} /> Hide uncovered</label>
          <select className="input-field w-auto" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Tactics covered', `${data.summary.tacticsCovered} / 14`],
            ['Tactics with activity', `${data.summary.tacticsActive} / 14`],
            ['Techniques covered', `${data.summary.techniquesCovered} / ${data.summary.techniquesTotal}`],
            ['Techniques active', String(data.summary.techniquesActive)],
            ['Seen without a rule', String(data.summary.techniquesObserved)],
          ].map(([label, value]) => (
            <div key={label} className="card px-4 py-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-xl font-semibold text-gray-900 tabular-nums">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
        {(['active', 'observed', 'covered', 'uncovered'] as State[]).map(s => (
          <span key={s} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-4 h-3 rounded-sm ${tileClass({ id: '', name: '', rules: [], alerts: s === 'active' ? 5 : 0, cases: 0, state: s }, 10)}`} />
            {STATE_LABEL[s]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1 text-gray-400"><Info size={12} /> amber gets darker with more alerts; click a tile for details</span>
      </div>

      <div className="card p-3 overflow-x-auto">
        {loading && !data ? <p className="text-sm text-gray-400 p-4">Loading…</p> : data && (
          <div className="flex gap-2 min-w-max">
            {data.tactics.map(tactic => (
              <div key={tactic.name} className="w-40 shrink-0">
                <div className="mb-1.5 px-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 leading-tight">{tactic.name}</div>
                  <div className="text-[11px] text-gray-400 tabular-nums">{tactic.covered}/{tactic.total} covered{tactic.active ? ` · ${tactic.active} active` : ''}</div>
                </div>
                <div className="space-y-1">
                  {tactic.techniques.filter(t => !hideUncovered || t.state !== 'uncovered').map(t => (
                    <button key={t.id} onClick={() => setSelected({ tactic: tactic.name, technique: t })}
                      title={`${t.id} ${t.name} — ${STATE_LABEL[t.state]}${t.rules.length ? `\nRules: ${t.rules.join(', ')}` : ''}${t.alerts || t.cases ? `\n${t.alerts} alert(s), ${t.cases} case(s)` : ''}`}
                      className={`w-full text-left rounded px-1.5 py-1 leading-tight ${tileClass(t, maxAlerts)} ${selected?.technique.id === t.id ? 'ring-2 ring-primary-500' : ''}`}>
                      <div className="text-[10px] font-mono opacity-80">{t.id}</div>
                      <div className="text-[11px] font-medium truncate">{t.name}</div>
                      {(t.alerts > 0 || t.cases > 0) && <div className="text-[10px] tabular-nums opacity-90">{t.alerts} alerts · {t.cases} cases</div>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-4">
          <h2 className="font-medium text-sm text-gray-900 mb-2">Technique details</h2>
          {!selected ? <p className="text-sm text-gray-400">Click a tile in the matrix.</p> : (
            <div className="text-sm space-y-2">
              <div><span className="font-mono text-xs text-gray-500">{selected.technique.id}</span> <span className="font-medium text-gray-900">{selected.technique.name}</span> <span className="text-gray-400">· {selected.tactic}</span></div>
              <div className="text-gray-700">{STATE_LABEL[selected.technique.state]}{selected.technique.alerts || selected.technique.cases ? ` — ${selected.technique.alerts} alert(s), ${selected.technique.cases} case(s) in the last ${data?.days} days` : ''}.</div>
              {selected.technique.rules.length > 0
                ? <ul className="list-disc pl-5 text-gray-700">{selected.technique.rules.map(r => <li key={r}>{r}</li>)}</ul>
                : <p className="text-gray-500">No enabled rule references this technique. <Link to="/detection-rules" className="text-primary-700 underline">Create one</Link> with MITRE technique <code className="font-mono text-xs">{selected.technique.id}</code>.</p>}
            </div>
          )}
        </div>
        <div className="card p-4">
          <h2 className="font-medium text-sm text-gray-900 mb-2">Gaps to work on</h2>
          {data && (
            <div className="text-sm space-y-2">
              {gaps.length > 0
                ? <p className="text-gray-700">Tactics with no rule at all: <span className="font-medium">{gaps.join(', ')}</span>.</p>
                : <p className="text-gray-700">Every tactic has at least one rule.</p>}
              {observed.length > 0 ? (
                <div>
                  <p className="text-gray-700">Seen through a vendor but without a rule of ours (a rule here turns visibility into detection you control):</p>
                  <ul className="list-disc pl-5 text-gray-700">{observed.slice(0, 8).map(o => <li key={`${o.tactic}-${o.id}`}><span className="font-mono text-xs">{o.id}</span> {o.name} — {o.cases} case(s)</li>)}</ul>
                </div>
              ) : <p className="text-gray-500">Nothing observed without a rule in this period.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
